/**
 * The ligand sections of the MD analysis: RMSF per heavy atom, and the ligand
 * properties (RMSD, radius of gyration, intramolecular H-bonds, MolSA, SASA,
 * PSA) over time with histograms. Each is an `AnalysisChart` fed by a pure
 * option builder (integrations/mdCharts.js); reports reuse the same builders.
 */
import { useMemo, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';

import Select from 'BMapsUI/common/Select';
import EChart from 'BMapsSrc/ui/charting/EChart';
import {
    LIGAND_PROPERTIES_CAPTION, LIGAND_RMSF_CAPTION, ligandPropertiesChartOption,
    ligandPropertiesSize, ligandPropertyRuns, ligandRmsfChartOption, ligandRmsfRuns,
} from 'BMapsSrc/integrations/mdCharts';

/**
 * A titled chart with a choice of which runs to show, and a caption.
 * @param {object} props
 * @param {string} props.id
 * @param {string} props.title
 * @param {{ value: string, label: string }[]} props.choices - the run choices
 * @param {Function} props.buildOption - `(run, textColor) => ECharts option or null`
 * @param {number} props.height - chart height in px
 * @param {string} props.caption
 * @param {string} props.ariaLabel
 */
export function AnalysisChart({
    id, title, choices, buildOption, height, caption, ariaLabel,
}) {
    const theme = useTheme();
    const [run, setRun] = useState(choices[0].value);
    const textColor = theme?.palette?.text?.primary || '#444';
    const option = useMemo(() => buildOption(run, textColor), [buildOption, run, textColor]);
    if (!option) return null;
    return (
        <Box id={id} sx={{ marginTop: '0.75rem' }}>
            <Typography component="div" sx={{ fontWeight: 600 }}>
                {title}
            </Typography>
            {choices.length > 1 && (
                <Select
                    id={`${id}-run`}
                    label="Runs shown"
                    value={run}
                    onChange={(e) => setRun(e.target.value)}
                    sx={{ width: '100%', margin: '0.25rem 0' }}
                >
                    {choices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </Select>
            )}
            <EChart option={option} height={height} ariaLabel={ariaLabel} />
            <Typography variant="caption" component="div" sx={{ opacity: 0.8 }}>
                {caption}
            </Typography>
        </Box>
    );
}

export function LigandRmsfChart({ compound }) {
    const runs = useMemo(() => ligandRmsfRuns(compound), [compound]);
    const choices = useMemo(() => [
        { value: 'mean', label: runs.length > 1 ? 'Mean of all runs' : 'The run' },
        ...(runs.length > 1 ? runs.map((r) => ({ value: String(r), label: `Run ${r}` })) : []),
    ], [runs]);
    const buildOption = useMemo(() => (run, textColor) => ligandRmsfChartOption(
        compound, { run: run === 'mean' ? 'mean' : Number(run), textColor },
    ), [compound]);
    if (runs.length === 0) return null;
    return (
        <AnalysisChart
            id={`md-lrmsf-${compound.name}`}
            title="Ligand RMSF"
            choices={choices}
            buildOption={buildOption}
            height={240}
            caption={LIGAND_RMSF_CAPTION}
            ariaLabel={`Ligand RMSF of ${compound.name}`}
        />
    );
}

export function LigandPropertiesChart({ compound }) {
    const { panels, runs } = useMemo(() => ligandPropertyRuns(compound), [compound]);
    const choices = useMemo(() => [
        { value: 'all', label: runs.length > 1 ? 'All independent runs' : 'The run' },
        ...(runs.length > 1 ? runs.map((r) => ({ value: String(r.run), label: `Run ${r.run}` })) : []),
    ], [runs]);
    const buildOption = useMemo(() => (run, textColor) => ligandPropertiesChartOption(
        compound, { run: run === 'all' ? 'all' : Number(run), textColor },
    ), [compound]);
    // The RMSD alone is the protein-ligand RMSD chart's: show this only with other properties.
    if (panels.filter((p) => p.key !== 'ligand_rmsd_a').length === 0) return null;
    return (
        <AnalysisChart
            id={`md-lprops-${compound.name}`}
            title="Ligand properties"
            choices={choices}
            buildOption={buildOption}
            height={ligandPropertiesSize(panels.length, true).height}
            caption={LIGAND_PROPERTIES_CAPTION}
            ariaLabel={`Ligand properties of ${compound.name}`}
        />
    );
}
